import showdown from 'showdown';
import router from '@/router/';
import apiCall from '@/utils/apiCall';
import {
    find,
    remove,
} from 'lodash';
import checkUserSubscriptions from '@/utils/checkUserSubscriptions';
import getActiveSubscription from '@/utils/getActiveSubscription';

export default {
    /**
     * Logout user
     *
     * @param    {object}   state           Vuex state.
     * @param   {Function}  state.commit    Commit mutation.
     */
    async logOut({ commit }) {
        await apiCall(this._vm, 'post', '/api/user/logout');

        commit('resetState');
        router.push('/login').catch(() => {});
    },

    /**
     * Handle user logout if session expired.``
     *
     * @param    {object}   state           Vuex state.
     * @param   {Function}  state.commit    Commit mutation.
     */
    handleExpiredLogin({ commit }) {
        commit('resetState');
        commit('setLoginRedirectUrl', router.currentRoute.path);
        router.push('/login').catch(() => {});
    },

    /**
     * Loads user data from API
     *
     * @param    {object}   state           Vuex state.
     * @param   {Function}  state.commit    Commit mutation.
     *
     */
    async loadUser({ commit, state }) {
        if (state.user) {
            return;
        }

        const response = await apiCall(this._vm, 'get', '/api/user');
        const { data, status } = response;

        if (200 === status) {
            commit('setUser', data);
        }
    },

    /**
     * Get user data from API
     *
     * @param    {object}   state           Vuex state.
     * @param   {Function}  state.commit    Commit mutation.
     *
     */
    async updateUser({ commit }) {
        const response = await apiCall(this._vm, 'get', '/api/user');
        const { data, status } = response;

        if (200 === status) {
            commit('setUser', data);
            return true;
        }
    },

    /**
     * Sets register processing flag
     *
     * @param   {object}  {state}  Vuex state.
     * @param   {boolean}  payload   Register processing flag.
     *
     */
    registeringUser({ commit }, payload) {
        commit('registerProcessing', payload);
    },

    /**
     * Sets new avatar url
     *
     * @param   {object}  {state}  Vuex state.
     * @param   {string}  payload  New avatar url.
     */
    setNewAvatar({ commit }, payload) {
        commit('newAvatar', payload);
    },

    /**
     * Sets user licenses
     *
     * @param    {object}   state   Vuex state.
     * @param   {Function}  state.commit  Commit mutation.
     * @param {boolean} reload Flag to reload state licenses.
     *
     */
    async loadLicenses({ state, commit }, reload) {
        if (! reload && null !== state.licenses) {
            return;
        }

        const { status, data } = await apiCall(this._vm, 'get', '/api/licenses');

        for (const license of data) {
            const subscription = find(state.user.subscriptions, { id: license.subscription_id });

            license.type = subscription?.name || 'pro';
        }

        if (200 === status) {
            commit('setLicenses', data);
        }
    },

    /**
     * Update license website
     *
     * @param    {object}   state   Vuex state.
     * @param   {Function}  state.commit  Commit mutation.
     * @param   {object}   licenses_data  Licenses data.
     */
    async updateLicenseWebsite({ state, commit }, licenses_data) {
        const { index, site } = licenses_data;
        const licenses = { ...state.licenses };
        const { websites } = licenses[index];

        remove(websites, (website) => {
            return website.id === site.id;
        });

        if (licenses[index].seats <= websites.length - 1) {
            commit('updateLicenses', licenses);
        } else {
            commit('updateLicenses', licenses);
            licenses[index].free_seats++;
        }
    },

    /**
     * Sets user meta
     *
     * @param   {object}   params        Params.
     * @param   {Function} params.commit Commit mutation.
     * @param   {Function} params.state  Vuex state.
     * @param   {object}   meta          ser meta.
     *
     */
    async setUserMeta({ commit, state }, meta) {
        const response = await apiCall(this._vm, 'put', '/api/user/meta', meta);

        if (200 === response.status && response.data) {
            commit('replaceUserMeta', {
                ...state.user.meta_data,
                ...meta,
            });
        }
    },

    /**
     * Sets user meta
     *
     * @param   {object}   params        Params.
     * @param   {Function} params.commit Commit mutation.
     * @param   {Function} params.state  Vuex state.
     * @param   {object}   meta          ser meta.
     *
     */
    async deleteUserMeta({ commit, state }, meta) {
        const response = await apiCall(this._vm, 'delete', '/api/user/meta', { data: meta });

        if (200 === response.status) {
            const newMeta = { ...state.user.meta_data };

            for (const item of meta) {
                delete newMeta[item];
            }

            commit('replaceUserMeta', newMeta);
        }
    },

    /**
     * Load products from API.
     *
     * @param    {object}   state   Vuex state.
     * @param   {Function}  state.commit  Commit mutation.
     *
     */
    async loadProducts({ state, commit }) {
        if (null !== state.products) {
            return;
        }

        const { data } = await apiCall(this._vm, 'get', '/api/products');
        const converter = new showdown.Converter();

        for (const product of data) {
            product.changelog = converter.makeHtml(product.changelog);
        }

        commit('setProducts', data);
    },

    /**
     * Sets user card
     *
     * @param   {object}   params        Params.
     * @param   {Function} params.commit Commit mutation.
     * @param    {object}   params.state   Vuex state.
     *
     */
    loadUserCard({
        commit,
        dispatch,
        state,
    }) {
        commit('setUserCard', null);

        if (! state.paymentMethods) {
            dispatch('loadPaymentMethods');
            return;
        }

        if (! state.userSubscription) {
            return;
        }

        const { default_payment_method } = state.userSubscription;

        for (const card of state.paymentMethods) {
            if (card.id === default_payment_method) {
                commit('setUserCard', card);
                return;
            }
        }

        commit('setUserCard', false);
    },

    /**
     * Load payment methods.
     *
     * @param {object}   params         Params.
     * @param {Function} params.commit  Commit mutation.
     * @param {object}   params.state   Vuex state.
     */
    async loadPaymentMethods({
        commit,
        dispatch,
        state,
    }) {
        if (state.paymentMethods) {
            return;
        }

        const { status, data } = await apiCall(this._vm, 'get', '/api/billing/payments/methods');

        if (200 === status) {
            commit('setPaymentMethods', data);
            dispatch('loadUserCard');
        }
    },

    /**
     * Reloads payment methods.
     *
     * @param {object}   params         Params.
     * @param {Function} params.commit  Commit mutation.
     * @param {object}   params.state   Vuex state.
     */
    async reloadPaymentMethods({ commit, dispatch }) {
        commit('setPaymentMethods', null);
        dispatch('loadUserSubscription', { force: true });
    },

    /**
     * Load invoices.
     *
     * @param {object}   params         Params.
     * @param {Function} params.commit  Commit mutation.
     * @param {object}   params.state   Vuex state.
     */
    async loadInvoices({
        commit,
        state,
    }, { force = false } = {}) {
        if (null === state.invoices || true === force) {
            commit('setInvoices', null);
            const { status, data } = await apiCall(this._vm, 'get', `/api/billing/subscriptions/${state.currentSubscription.stripe_id}/invoices`);

            const invoices = 200 === status ? data : false;
            commit('setInvoices', invoices);
        }
    },

    /**
     * Sets user subscriptions
     *
     * @param {object}   params        Params.
     * @param {Function} params.commit Commit mutation.
     * @param {object}   params.state  Vuex state.
     * @param {number}   id            Subscription ID (optional).
     */
    async loadUserSubscription(
        {
            commit,
            state,
            dispatch,
        },
        {
            id = false,
            force = false,
        } = {}
    ) {
        const { subscriptions } = state.user;
        const lifetimeSubscription = checkUserSubscriptions(subscriptions);
        let subscription;

        if (lifetimeSubscription) {
            subscription = lifetimeSubscription;
        } else {
            subscription = getActiveSubscription(subscriptions);
        }

        if (false !== id) {
            const foundSubscription = find(subscriptions, { id });

            if (foundSubscription) {
                subscription = foundSubscription;
            }
        }

        if (true !== force && state.currentSubscription && state.currentSubscription.id === subscription.id) {
            return;
        }

        commit('setCurrentSubscription', subscription);
        commit('setUserSubscription', null);
        commit('setUserCard', null);
        commit('setInvoices', null);

        if ('none' === subscription.stripe_id) {
            commit('setUserSubscription', false);
            commit('setUserCard', false);
            return;
        }

        const { status, data } = await apiCall(this._vm, 'get', `/api/billing/subscriptions/${subscription.id}`);

        if (200 !== status) {
            return;
        }

        data.dbId = subscription.id;
        data.name = subscription.name;

        if (null === data.default_payment_method && 'pro' === subscription.name) {
            try {
                const {
                    status: paymentMethodStatus,
                    data: paymentMethodData,
                } = await apiCall(this._vm, 'get', '/api/billing/payments/methods/default');

                if (200 === paymentMethodStatus && paymentMethodData.id) {
                    data.default_payment_method = paymentMethodData.id;
                }
            } catch (e) {} // eslint-disable-line no-empty
        }

        commit('setUserSubscription', data);
        dispatch('loadUserCard');
    },

    /**
     * Loads upcoming user invoice
     *
     * @param {object}   params        Params.
     * @param {Function} params.commit Commit mutation.
     * @param {object}   params.state  Vuex state.
     * @param {object}   planData      Plan data to evaluate incoming invoice.
     */
    async loadUpcomingInvoice({
        commit,
        state,
    }, {
        quantity,
        plan,
        presumed = false,
    } = {}) {
        const key = presumed ? 'setPresumedInvoice' : 'setUpcomingInvoice';

        commit(key, null);

        const subscriptionId = state.currentSubscription.id;
        let route = `/api/billing/subscriptions/${subscriptionId}/invoices/preview`;

        if (quantity && plan) {
            route += `?plan=${plan}&quantity=${quantity}`;
        }

        if (presumed) {
            route += '&invoice=1';
        }

        try {
            const response = await apiCall(this._vm, 'get', route);

            if (200 === response.status) {
                commit(key, response.data);
            } else {
                commit(key, false);
            }
        } catch (e) {
            commit(key, false);
        }
    },

    /**
     * Load profile controls
     *
     * @param  {object}    store        Vuex store.
     * @param  {object}    store.state  Vuex state.
     * @param  {Function}  store.commit Method to commit a mutation.
     * @returns {Promise}               Promise.
     */
    async loadProfileControls({ state, commit }) {
        if ('object' === typeof state.profileControls && null !== state.profileControls) {
            return;
        }

        let profileControls = false;

        const { status, data } = await apiCall(this._vm, 'get', '/api/user/profile-controls/onboarding');

        if (200 === status && data) {
            profileControls = data;
        }

        commit('setProfileControls', profileControls);
    },
};
